import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/config/api';
import { queryClient } from '@/config/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface User {
  id: number;
  username: string;
  display_name: string;
  email: string;
  role: string;
  permissions: string[];
  mfa_enabled: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  permissions: string[];
  token: string | null;

  login: (user: User, token?: string) => void;
  logout: () => void;
  hasPermission: (perm: string) => boolean;
  updateUser: (user: Partial<User>) => void;
}

// ---------------------------------------------------------------------------
// Store
//
// JWT tokens are now stored in httpOnly cookies set by the server.
// localStorage is only used for non-sensitive user profile info so that
// the UI can render immediately on page load without an extra /auth/me call.
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      permissions: [],
      token: null,

      login: (user: User, token?: string) => {
        set({
          user,
          isAuthenticated: true,
          permissions: user.permissions,
          token: token || null,
        });
      },

      logout: () => {
        api.post('/auth/logout').catch(() => {});
        queryClient.clear();
        set({
          user: null,
          isAuthenticated: false,
          permissions: [],
          token: null,
        });
      },

      hasPermission: (perm: string) => {
        const { permissions, user } = get();
        if (user?.role === 'admin') return true;
        if (permissions.includes('*')) return true;
        if (permissions.includes(perm)) return true;
        const [mod, action] = perm.split('.');
        if (mod && permissions.includes(`${mod}.*`)) return true;
        if (action && permissions.includes(`*.${action}`)) return true;
        return false;
      },

      updateUser: (partial: Partial<User>) => {
        const current = get().user;
        if (current) {
          set({ user: { ...current, ...partial } });
        }
      },
    }),
    {
      name: 'freshops-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
        token: state.token,
      }),
    },
  ),
);
